import {useState, useEffect, useMemo, useRef} from 'react';

/**
 * Generic result of fetching data for multiple items in parallel
 */
export interface MultipleParallelFetchResult<T, K extends string> {
    /**
     * Map of keys to data items
     * Access data for a specific key using: dataMap[key]
     */
    data: Record<K, T>;

    /**
     * Whether any requests are currently loading
     */
    isLoading: boolean;

    /**
     * General error that occurred during fetching (if any)
     */
    error: Error | undefined;

    /**
     * Map of keys to specific errors that occurred for that key's request
     */
    errorMap: Record<K, Error>;
}

/**
 * Generic hook for fetching multiple items in parallel and storing results in a map
 * @param configs - Array of configuration items
 * @param fetchFn - Function that takes a config item and returns a promise of data and key
 * @returns Object containing mapping of keys to data, loading state, and error state
 */
export function useParallelFetch<T, C, K extends string = string>(
    configs: C[],
    fetchFn: (config: C) => Promise<{key: K; data: T}>,
): MultipleParallelFetchResult<T, K> {
    const [dataMap, setDataMap] = useState<Record<K, T>>({} as Record<K, T>);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [errorMap, setErrorMap] = useState<Record<K, Error>>({} as Record<K, Error>);

    // Create a unique key for the entire configuration array to avoid unnecessary rerenders
    const configsKey = useMemo(() => JSON.stringify(configs), [configs]);

    // Store fetchFn in a ref to avoid dependency issues
    const fetchFnRef = useRef(fetchFn);

    // Update the ref when fetchFn changes
    useEffect(() => {
        fetchFnRef.current = fetchFn;
    }, [fetchFn]);

    // Fetch data for each item
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(undefined);
            setErrorMap({} as Record<K, Error>);

            const newDataMap: Record<K, T> = {} as Record<K, T>;
            const newErrorMap: Record<K, Error> = {} as Record<K, Error>;

            try {
                // Process all configs in parallel
                const results = await Promise.allSettled(
                    configs.map(async (config) => {
                        return await fetchFnRef.current(config);
                    }),
                );

                // Process results
                results.forEach((result, index) => {
                    if (result.status === 'fulfilled') {
                        const {key, data} = result.value;
                        newDataMap[key] = data;
                    } else {
                        // We don't have the key from the failed fetch,
                        // so we need to try to extract it from the config
                        try {
                            // This assumes the configs have a property that can be used as a key
                            // The caller needs to ensure this works with their config type
                            const configItem = configs[index] as unknown;
                            const keyValue =
                                configItem &&
                                typeof configItem === 'object' &&
                                'userId' in configItem
                                    ? (configItem.userId as unknown as K)
                                    : (`error-${index}` as unknown as K);

                            newErrorMap[keyValue] =
                                result.reason instanceof Error
                                    ? result.reason
                                    : new Error(`Failed to fetch data for item ${index}`);
                        } catch {
                            // If we can't get a key, use the index
                            const fallbackKey = `error-${index}` as unknown as K;
                            newErrorMap[fallbackKey] =
                                result.reason instanceof Error
                                    ? result.reason
                                    : new Error(`Failed to fetch data for item ${index}`);
                        }
                    }
                });

                // Set general error if any requests failed
                if (Object.keys(newErrorMap).length > 0) {
                    setError(
                        new Error(
                            `Failed to fetch data for ${Object.keys(newErrorMap).length} item(s)`,
                        ),
                    );
                    setErrorMap(newErrorMap);
                }

                setDataMap(newDataMap);
            } catch (err) {
                // This catch block handles any unhandled errors in the try block
                setError(err instanceof Error ? err : new Error('An unknown error occurred'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [configs, configsKey]);

    return {
        data: dataMap,
        isLoading,
        error,
        errorMap,
    };
}
